import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useEffect, useState } from "react";

/** Marketing top-nav — single source of truth for the public IA.
 *
 *  IA (approved by product):
 *    Home · About Us · Products & Services · How It Works · Features · Pricing · Help
 *
 *  Notes for future maintainers:
 *    - Kept the `variant="dark"` overload because the landing hero renders
 *      on a navy gradient, and Pricing/About/Help render on white. Both
 *      need the SAME link set — only palette differs.
 *    - Route targets:
 *        /                       -> Home (landing)
 *        /about                  -> About page (LegalLayout — unchanged)
 *        /products               -> CmsPage(slug=products-and-services)
 *        /how-it-works           -> Animated HowItWorks page
 *        /features               -> CmsPage(slug=features)
 *        /pricing                -> Pricing page (unchanged)
 *        /help                   -> Help (FAQ + ticket form) (unchanged)
 *    - Mobile menu closes on route change and locks scroll while open.
 */
const NAV_LINKS = [
    { to: "/",              label: "Home",                testId: "nav-home",     end: true },
    { to: "/about",         label: "About Us",            testId: "nav-about-us" },
    { to: "/products",      label: "Products & Services", testId: "nav-products" },
    { to: "/how-it-works",  label: "How It Works",        testId: "nav-how-it-works" },
    { to: "/features",      label: "Features",            testId: "nav-features" },
    { to: "/pricing",       label: "Pricing",             testId: "nav-pricing" },
    { to: "/help",          label: "Help",                testId: "nav-help" },
];

export default function MarketingNav({ variant = "light" }) {
    const [open, setOpen] = useState(false);
    const isDark = variant === "dark";

    // Lock body scroll while the mobile menu is open, restore on close.
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, [open]);

    const linkClass = ({ isActive }) => {
        const base = "text-sm font-medium transition-colors ";
        if (isDark) {
            return base + (isActive ? "text-white" : "text-white/75 hover:text-white");
        }
        return base + (isActive ? "text-[#123B5D]" : "text-[#17212B]/70 hover:text-[#123B5D]");
    };

    return (
        <header
            className={
                "w-full border-b sticky top-0 z-40 transition-colors " +
                (isDark
                    ? "border-white/10 bg-[#0F1B2D]/85 backdrop-blur"
                    : "border-[#123B5D]/10 bg-white/90 backdrop-blur")
            }
            data-testid="marketing-nav"
        >
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                <Link
                    to="/"
                    className={
                        "flex items-center gap-2.5 font-bold text-lg " +
                        (isDark ? "text-white" : "text-[#123B5D]")
                    }
                    data-testid="nav-logo"
                >
                    <img
                        src="/brand-icon.png"
                        alt="Doctor Digital ID"
                        className={
                            "h-9 w-9 object-contain " +
                            (isDark ? "" : "rounded-lg bg-white/60 p-0.5")
                        }
                    />
                    <span className="tracking-tight">
                        DOCTOR <span className="text-[#2563EB]">DIGITAL ID</span>
                    </span>
                </Link>

                <nav className="hidden lg:flex items-center gap-7">
                    {NAV_LINKS.map((l) => (
                        <NavLink key={l.to} to={l.to} end={l.end} className={linkClass} data-testid={l.testId}>
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    <Link
                        to="/login"
                        className={
                            "text-sm font-semibold " +
                            (isDark ? "text-white" : "text-[#123B5D]")
                        }
                        data-testid="nav-login"
                    >
                        Log in
                    </Link>
                    <Link to="/signup" className="ddi-teal-btn text-sm" data-testid="nav-signup">
                        Create Identity
                    </Link>
                </div>

                <button
                    className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-md"
                    onClick={() => setOpen((v) => !v)}
                    style={{ color: isDark ? "white" : "#123B5D" }}
                    aria-label={open ? "Close menu" : "Open menu"}
                    aria-expanded={open}
                    data-testid="nav-menu-toggle"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {/* Mobile menu — full-width panel below the header. */}
            {open ? (
                <div
                    className="lg:hidden border-t border-[#123B5D]/10 bg-white shadow-lg"
                    data-testid="nav-mobile-menu"
                >
                    <div className="px-6 py-4 flex flex-col gap-1">
                        {NAV_LINKS.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.end}
                                onClick={() => setOpen(false)}
                                className={({ isActive }) =>
                                    "py-2.5 text-[15px] font-medium " +
                                    (isActive ? "text-[#123B5D]" : "text-[#17212B]/85")
                                }
                                data-testid={`${l.testId}-mob`}
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <div className="flex gap-3 pt-3 border-t border-[#123B5D]/10 mt-1">
                            <Link
                                to="/login"
                                onClick={() => setOpen(false)}
                                className="flex-1 text-center text-sm py-2 rounded-full border border-[#123B5D] text-[#123B5D] font-semibold"
                                data-testid="nav-login-mob"
                            >
                                Log in
                            </Link>
                            <Link
                                to="/signup"
                                onClick={() => setOpen(false)}
                                className="flex-1 text-center text-sm py-2 rounded-full bg-[#0F766E] text-white font-semibold"
                                data-testid="nav-signup-mob"
                            >
                                Create Identity
                            </Link>
                        </div>
                    </div>
                </div>
            ) : null}
        </header>
    );
}
